<!-- Left: Canvas Sidebar (on background layer) -->
<div class="sidebar-canvas-wrapper sidebar-mode">
  <div class="sidebar-header-background">
    <h2 [style.font-size]="projectTitleFontSize">{{ projectName || 'Project Canvas' }}</h2>
    <button mat-icon-button class="header-action-btn"
            *ngIf="project"
            (click)="editProject()"
            matTooltip="Edit project details"
            aria-label="Edit project details">
      <mat-icon svgIcon="pencil"></mat-icon>
    </button>
  </div>
  <!-- Left: Canvas (compact view) -->
  <div class="sidebar-canvas" [class.content-hidden]="isContentHidden">
    <div class="project-canvas-container">
          <!-- Loading indicator -->
          <div *ngIf="loading" class="loading-container">
            <mat-spinner diameter="50"></mat-spinner>
            <p>Loading project...</p>
          </div>

          <!-- Canvas content (same as below but compact) -->
          <div *ngIf="!loading" class="canvas-workflow sidebar-layout">
            <!-- Show empty state when error occurred (not cleaning errors, those are shown in the right panel) -->
            <div *ngIf="error && cleaningState !== 'error'" class="empty-state">
              <button mat-raised-button color="primary" (click)="goBack()">
                <mat-icon svgIcon="arrow-left"></mat-icon>
                Go Back to Activity
              </button>
            </div>
            
            <ng-container *ngIf="(!error || cleaningState === 'error') && project">
            <!-- Column 1: Get Data Section -->
            <div class="collapsible-section">
              <div class="section-header-collapsible" (click)="toggleDataSourceSection()">
                <mat-icon class="expand-icon">{{ isDataSourceSectionExpanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                <h4>Step 1: Get Data</h4>
                <mat-icon *ngIf="projectDataSource" class="section-complete-icon" svgIcon="check_circle"></mat-icon>
              </div>
              <div class="section-content" *ngIf="isDataSourceSectionExpanded">
                <div class="workflow-column get-data-column">
                  <div class="data-source-card">
                    <div *ngIf="projectDataSource" 
                         class="workflow-card canvas-card data-card completed" 
                         [class.highlighted]="highlightedItemId === projectDataSource.dataSourceId?.toString()"
                         [class.waiting]="isApiListenerWaitingForData()"
                         (click)="editDataSource()">
                      <div class="card-header">
                        <mat-icon class="card-icon" svgIcon="data-set"></mat-icon>
                        <div class="card-text-content">
                          <span class="card-title">{{ projectDataSource.dataSourceName }}</span>
                          <p *ngIf="hasValidSchema()" class="card-subtitle">Data Source • {{ getSchemaDisplayText() }}</p>
                          <p *ngIf="!hasValidSchema() && !isApiListenerWaitingForData()" class="card-subtitle">Data Source</p>
                          <p *ngIf="isApiListenerWaitingForData()" class="card-subtitle waiting-text">API Listener is waiting for data</p>
                        </div>
                        <mat-icon *ngIf="isApiListenerWaitingForData()" class="status-icon waiting-icon">schedule</mat-icon>
                      </div>
                    </div>

                    <!-- Pending data source card -->
                    <div *ngIf="!projectDataSource && hasPendingDataSource" 
                         class="workflow-card canvas-card data-card pending highlighted">
                      <div class="card-header">
                        <mat-icon class="card-icon" svgIcon="data-set"></mat-icon>
                        <div class="card-text-content">
                          <span class="card-title">New Data Source</span>
                          <p class="card-subtitle">Not yet created...</p>
                        </div>
                      </div>
                    </div>

                    <div *ngIf="!projectDataSource && !hasPendingDataSource" 
                         class="workflow-card canvas-card data-card add-card" 
                         (click)="addDataSource()" 
                         id="new-datasource-button">
                      <mat-icon class="card-icon" svgIcon="data-set"></mat-icon>
                      <span class="card-title">New Data Source</span>
                      <mat-icon class="add-icon" svgIcon="add_circle"></mat-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Column 2: Select Goals Section -->
            <div class="collapsible-section">
              <div class="section-header-collapsible" (click)="toggleGoalsSection()">
                <mat-icon class="expand-icon">{{ isGoalsSectionExpanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                <h4>Step 2: Select Goals <span class="section-count" *ngIf="projectGoals.length > 0">({{ projectGoals.length }})</span></h4>
                <mat-icon *ngIf="projectGoals.length > 0" class="section-complete-icon" svgIcon="check_circle"></mat-icon>
              </div>
              <div class="section-content" *ngIf="isGoalsSectionExpanded">
                <div class="workflow-column goals-column">
                  <div class="goals-container"
                       cdkDropList
                       (cdkDropListDropped)="onGoalDropped($event)">
                    <div *ngFor="let goal of projectGoals; let goalIndex = index" class="goal-row"
                         cdkDrag
                         [cdkDragDisabled]="projectGoals.length < 2">
                      <div class="workflow-card canvas-card goal-card completed"
                           [class.highlighted]="highlightedItemId === goal.goalId?.toString()"
                           [class.inactive]="!goal.isActive"
                           (click)="editGoal(goal)">
                        <div class="card-header">
                          <span class="drag-handle" cdkDragHandle
                                (click)="$event.stopPropagation()"
                                [style.opacity]="projectGoals.length < 2 ? '0.5' : '1'"
                                title="Drag to reorder">
                            <mat-icon>drag_indicator</mat-icon>
                          </span>
                          <mat-icon class="card-icon" svgIcon="flag"></mat-icon>
                          <div class="card-text-content">
                            <span class="card-title">{{ goal.goalName }}</span>
                            <p class="card-subtitle" *ngIf="getTasksForGoal(goal).length > 0">{{ getTasksForGoal(goal).length }} Task{{ getTasksForGoal(goal).length !== 1 ? 's' : '' }}</p>
                          </div>
                        </div>
                        <div class="card-actions">
                          <button mat-icon-button class="action-icon-btn remove-btn" 
                                  (click)="removeGoal(goal); $event.stopPropagation()"
                                  title="Delete goal">
                            <mat-icon svgIcon="delete"></mat-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Pending goal card -->
                    <div *ngIf="hasPendingGoal" class="goal-row">
                      <div class="workflow-card canvas-card goal-card pending highlighted">
                        <div class="card-header">
                          <mat-icon class="card-icon" svgIcon="flag"></mat-icon>
                          <div class="card-text-content">
                            <span class="card-title">New Goal</span>
                            <p class="card-subtitle">Not yet created...</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div class="add-goal-section" *ngIf="!hasPendingGoal">
                      <div class="workflow-card canvas-card goal-card add-card" 
                           [class.disabled]="!projectDataSource"
                           (click)="projectDataSource ? addGoal() : null">
                        <mat-icon class="card-icon" svgIcon="flag"></mat-icon>
                        <span class="card-title">Add Goal</span>
                        <mat-icon class="add-icon" svgIcon="add_circle"></mat-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Column 3: Output Section -->
            <div class="collapsible-section">
              <div class="section-header-collapsible" (click)="toggleDestinationsSection()">
                <mat-icon class="expand-icon">{{ isDestinationsSectionExpanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                <h4>Step 3: Set Destinations <span class="section-count" *ngIf="projectDataDestinations.length > 0">({{ projectDataDestinations.length }})</span></h4>
                <mat-icon *ngIf="projectDataDestinations.length > 0" class="section-complete-icon" svgIcon="check_circle"></mat-icon>
              </div>
              <div class="section-content" *ngIf="isDestinationsSectionExpanded">
                <div class="workflow-column output-column">
                  <div class="destinations-container">
                    <div *ngFor="let destination of projectDataDestinations; let destIndex = index" class="destination-row">
                      <div class="workflow-card canvas-card output-card completed"
                           [class.highlighted]="highlightedItemId === destination.dataDestinationId.toString()"
                           (click)="editDestination(destination)">
                        <div class="card-header">
                          <mat-icon class="card-icon" svgIcon="download-right"></mat-icon>
                          <div class="card-text-content">
                            <span class="card-title">{{ destination.destinationTypeName }} - {{ destination.destinationFormatTypeName }}</span>
                            <p class="card-subtitle">{{ getDestinationDescription(destination) }}</p>
                          </div>

                          <div class="card-actions">
                            <button mat-icon-button class="action-icon-btn remove-btn"
                                    (click)="removeOutput(destination); $event.stopPropagation()"
                                    title="Delete output">
                              <mat-icon svgIcon="delete"></mat-icon>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Pending destination card -->
                    <div *ngIf="hasPendingDestination" class="destination-row">
                      <div class="workflow-card canvas-card output-card pending highlighted">
                        <div class="card-header">
                          <mat-icon class="card-icon" svgIcon="download-right"></mat-icon>
                          <div class="card-text-content">
                            <span class="card-title">New Output</span>
                            <p class="card-subtitle">Not yet created...</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="add-destination-section" *ngIf="!hasPendingDestination">
                      <div class="workflow-card canvas-card output-card add-card" 
                           [class.disabled]="projectGoals.length === 0"
                           (click)="projectGoals.length > 0 ? addOutput() : null">
                        <mat-icon class="card-icon" svgIcon="download-right"></mat-icon>
                        <span class="card-title">Add Output</span>
                        <mat-icon class="add-icon" svgIcon="add_circle"></mat-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- View Outputs Section -->
            <div class="collapsible-section">
              <div class="section-header-collapsible" (click)="toggleOutputsSection()">
                <mat-icon class="expand-icon">{{ isOutputsSectionExpanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                <h4>Step 4: Start Cleaning</h4>
              </div>
              <div class="section-content" *ngIf="isOutputsSectionExpanded">
                <div class="workflow-column output-column">
                  <!-- Start Pipeline Section -->
                  <div class="start-pipeline-section">
                    <div class="workflow-card canvas-card start-pipeline-card"
                         [class.completed]="projectDataDestinations.length > 0"
                         [class.disabled]="projectDataDestinations.length === 0"
                         (click)="hasCleaningResults && !hasProjectConfigChanged() ? onViewCleaningResults() : (projectDataDestinations.length > 0 ? onStartPipeline() : null)">
                      <div class="card-header">
                        <mat-icon class="card-icon" [svgIcon]="hasCleaningResults && !hasProjectConfigChanged() ? 'visibility' : 'play'"></mat-icon>
                        <div class="card-text-content">
                          <span class="card-title">{{ hasCleaningResults && !hasProjectConfigChanged() ? 'View Results' : 'Start Workflow' }}</span>
                          <p class="card-subtitle" *ngIf="hasCleaningResults && !hasProjectConfigChanged()">View cleaning results</p>
                          <p class="card-subtitle" *ngIf="(!hasCleaningResults || hasProjectConfigChanged()) && projectDataDestinations.length > 0">Run data processing</p>
                          <p class="card-subtitle" *ngIf="(!hasCleaningResults || hasProjectConfigChanged()) && projectDataDestinations.length === 0">Configure destinations first</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- View Outputs Section -->
                  <div class="view-outputs-section">
                    <div class="workflow-card canvas-card view-outputs-card"
                         [class.completed]="projectHasOutputs"
                         [class.disabled]="!projectHasOutputs"
                         [class.highlighted]="highlightedItemId === 'view-outputs'"
                         (click)="projectHasOutputs ? onViewOutputs() : null">
                      <div class="card-header">
                        <mat-icon class="card-icon" svgIcon="show"></mat-icon>
                        <div class="card-text-content">
                          <span class="card-title">View Outputs</span>
                          <p class="card-subtitle" *ngIf="projectHasOutputs">View processed data</p>
                          <p class="card-subtitle" *ngIf="!projectHasOutputs">No outputs yet</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>


<!-- Right: Editor Panel (raised content area) -->
<div class="content-wrapper-parent editor-wrapper-parent" [class.visible]="isEditorVisible" *ngIf="editorMode">
  <div class="content-wrapper sidebar-editor">
  <!-- Speech bubble tooltip for first-time users - positioned to align with New Data Source button -->
  <div class="speech-bubble-tooltip-editor" *ngIf="showDataSourceTooltip && editorMode === 'help'">
    <div class="tooltip-content">
      <strong>Start Here!</strong>
      <p>Click "New Data Source" to begin</p>
    </div>
    <div class="tooltip-arrow"></div>
  </div>

  <!-- Speech bubble tooltip for add-goal step - positioned to align with Add Goal card -->
  <div class="speech-bubble-tooltip-editor speech-bubble-tooltip-goal" *ngIf="showGoalTooltip && editorMode === 'help'">
    <div class="tooltip-content">
      <strong>Next Step!</strong>
      <p>Click "Add Goal" to continue</p>
    </div>
    <div class="tooltip-arrow"></div>
  </div>
  
  <div class="editor-header">
    <div class="editor-header-content">
      <h2>{{ editorTitle }}</h2>
      <p *ngIf="editorSubtitle" class="editor-subtitle">{{ editorSubtitle }}</p>
    </div>
    <button
      *ngIf="projectDataDestinations.length > 0"
      mat-button
      type="button"
      aria-label="View start workflow instructions"
      matTooltip="View start workflow instructions"
      (click)="openStartProjectInstructions()">
      View API Instructions
    </button>
  </div>
  <div class="editor-content">
    <!-- Help Content -->
    <ng-container *ngIf="editorMode === 'help'">
      <div class="canvas-help-content">
        <ng-container *ngIf="!editorData || !editorData.helpType || editorData.helpType === 'initial-canvas-help'">
          <!-- Initial help content - How the Project Canvas works -->
          
          <div class="help-steps">
            <div class="help-step">
              <div class="step-icon">
                <mat-icon *ngIf="!projectDataSource" svgIcon="number-1-grey"></mat-icon>
                <mat-icon *ngIf="projectDataSource" class="step-complete-icon" svgIcon="check_circle"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Get Data</div>
                <div class="step-description">Connect to your data source by uploading a file or setting up an API listener.</div>
              </div>
            </div>
            
            <mat-icon class="step-arrow">arrow_forward</mat-icon>
            
            <div class="help-step">
              <div class="step-icon">
                <mat-icon *ngIf="projectGoals.length === 0" svgIcon="number-2-grey"></mat-icon>
                <mat-icon *ngIf="projectGoals.length > 0" class="step-complete-icon" svgIcon="check_circle"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Select Goals</div>
                <div class="step-description">Define cleaning and transformation goals to achieve your objectives.</div>
              </div>
            </div>
            
            <mat-icon class="step-arrow">arrow_forward</mat-icon>
            
            <div class="help-step">
              <div class="step-icon">
                <mat-icon *ngIf="projectDataDestinations.length === 0" svgIcon="number-3-grey"></mat-icon>
                <mat-icon *ngIf="projectDataDestinations.length > 0" class="step-complete-icon" svgIcon="check_circle"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Set Destinations</div>
                <div class="step-description">Choose where your cleaned data should be sent - database, file, or API endpoint.</div>
              </div>
            </div>
            
            <mat-icon class="step-arrow">arrow_forward</mat-icon>
            
            <div class="help-step">
              <div class="step-icon">
                <mat-icon svgIcon="number-4-grey"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Run Workflow</div>
                <div class="step-description">Execute your workflow to process and clean your data, then review the results.</div>
              </div>
            </div>
          </div>
        </ng-container>
        
        <ng-container *ngIf="editorData && editorData.helpType === 'after-data-source'">
          <div class="help-section">
            <mat-icon class="help-icon success" svgIcon="check_circle"></mat-icon>
            <p>Great! You've set up your data source.</p>
            <p>Now you can add goals to define what cleaning and transformation operations you want to perform on your data.</p>
          </div>
          
          <div class="help-action">
            <button mat-raised-button color="primary" (click)="addGoal()">
              <mat-icon svgIcon="flag"></mat-icon>
              Add Your First Goal
            </button>
          </div>
        </ng-container>
        
        <ng-container *ngIf="editorData && editorData.helpType === 'after-goal'">
          <div class="help-section">
            <mat-icon class="help-icon success" svgIcon="check_circle"></mat-icon>
            <p>Excellent! Your goals are configured.</p>
            <p>You can create another goal, or set up destinations to specify where your cleaned data should be sent.</p>
          </div>

          <div class="help-action double-action">
            <button mat-raised-button color="primary" (click)="addGoal()">
              Add Another Goal
            </button>
            
            <span class="or-label" aria-hidden="true">or</span>

            <button mat-raised-button color="primary" (click)="addOutput()">
              Add Output
            </button>
          </div>
        </ng-container>
        
        <ng-container *ngIf="editorData && editorData.helpType === 'after-destination'">
          <div class="help-section">
            <mat-icon class="help-icon success" svgIcon="check_circle"></mat-icon>
            <p>Perfect! Your project is fully configured.</p>
            <p>You can add more destinations, or run your project to clean and transform your data.</p>
          </div>
          
          <div class="help-action double-action">
            <button mat-raised-button color="primary" (click)="addOutput()">
              Add More Destinations
            </button>

            <span class="or-label" aria-hidden="true">or</span>

            <button mat-raised-button color="primary" (click)="onStartPipeline()">
              Start Workflow
            </button>
          </div>
        </ng-container>
      </div>
    </ng-container>
    
    <!-- Data Source Editor (Inline Mode) -->
    <ng-container *ngIf="editorMode === 'data-source'">
      <app-new-data-source-dialog 
        [inlineData]="editorData"
        (inlineSubmit)="handleDataSourceSubmit($event)"
        (inlineCancel)="handleDataSourceCancel()"
        (titleChange)="onEditorTitleChange($event)"
        (subtitleChange)="onEditorSubtitleChange($event)"
        (dataSourceCreated)="handleDataSourceCreated($event)"
        (schemaDetected)="handleSchemaDetected($event)">
      </app-new-data-source-dialog>
    </ng-container>
    
    <!-- Data Source Details (Inline Mode) -->
    <ng-container *ngIf="editorMode === 'datasource-detail'">
      <app-datasource-info-panel 
        [dataSource]="editorData?.dataSource"
        (subtitleChange)="onEditorSubtitleChange($event)">
      </app-datasource-info-panel>
    </ng-container>
    
    <!-- Goal Editor (Inline Mode) -->
    <ng-container *ngIf="editorMode === 'goal'">
      <app-select-goal-dialog 
        [inlineData]="editorData"
        (inlineSubmit)="handleGoalSubmit($event)"
        (inlineCancel)="handleGoalCancel()"
        (titleChange)="onEditorTitleChange($event)"
        (subtitleChange)="onEditorSubtitleChange($event)">
      </app-select-goal-dialog>
    </ng-container>
    
    <!-- Destination Editor (Inline Mode) -->
    <ng-container *ngIf="editorMode === 'destination'">
      <app-set-destination-dialog 
        [inlineData]="editorData"
        (inlineSubmit)="handleDestinationSubmit($event)"
        (inlineCancel)="handleDestinationCancel()"
        (titleChange)="onEditorTitleChange($event)"
        (subtitleChange)="onEditorSubtitleChange($event)">
      </app-set-destination-dialog>
    </ng-container>
    
    <!-- Outputs Viewer (Inline Mode) -->
    <ng-container *ngIf="editorMode === 'outputs'">
      <app-outputs 
        [inlineMode]="true" 
        [inlineProjectId]="projectId || ''"
        (backClick)="onBackFromOutputs()">
      </app-outputs>
    </ng-container>

    <!-- Cleaning In Progress Panel -->
    <ng-container *ngIf="editorMode === 'cleaning'">
      <div class="cleaning-progress-panel">

        <!-- In Progress State -->
        <ng-container *ngIf="cleaningState === 'in-progress'">
          <div class="cleaning-state-icon cleaning-state-icon--running">
            <mat-spinner diameter="64"></mat-spinner>
          </div>
          <h3 class="cleaning-state-title">{{ cleaningMessage }}</h3>
          <p class="cleaning-state-description">
            The data cleaning workflow for <strong>{{ projectName }}</strong> is currently running.
          </p>
          <p class="cleaning-state-hint" *ngIf="cleaningMessage === 'Starting Clean'">
            Please wait while the workflow is being started...
          </p>
          <p class="cleaning-state-hint" *ngIf="cleaningMessage !== 'Starting Clean'">
            You're free to navigate away — the workflow will continue running in the background.
            <ng-container *ngIf="hasFileOutput">
              Once complete, the cleaned data file will be available on the View Outputs screen.
            </ng-container>
          </p>
          <div class="cleaning-progress-bar-container loading-progress-bar-container">
            <mat-progress-bar *ngIf="cleaningTotalSteps && (cleaningCurrentStep ?? 0) === 0" mode="indeterminate"></mat-progress-bar>
            <span *ngIf="cleaningTotalSteps && (cleaningCurrentStep ?? 0) === 0" class="cleaning-step-text">{{ cleaningMessage }}</span>
            <mat-progress-bar *ngIf="cleaningTotalSteps && (cleaningCurrentStep ?? 0) > 0" mode="determinate" [value]="((cleaningCurrentStep ?? 0) / cleaningTotalSteps) * 100"></mat-progress-bar>
            <span *ngIf="((cleaningCurrentStep ?? 0) > 0 && cleaningTotalSteps) || fetchingCleaningResults" class="cleaning-step-text">
              {{ fetchingCleaningResults ? 'Fetching Results' : ('Step ' + (cleaningCurrentStep ?? 0) + ' of ' + cleaningTotalSteps + (cleaningGoalName ? ' - ' + cleaningGoalName : '') + (cleaningColumnName ? ' (' + cleaningColumnName + ')' : '')) }}
            </span>
          </div>
          <div class="cleaning-state-actions" *ngIf="cleaningMessage !== 'Starting Clean'">
            <button mat-stroked-button class="cleaning-nav-btn" (click)="goToActivity()">
              <mat-icon svgIcon="docs"></mat-icon>
              Go to Activity
            </button>
          </div>
        </ng-container>

        <!-- Success State -->
        <ng-container *ngIf="cleaningState === 'success'">
          <div class="cleaning-state-icon cleaning-state-icon--success">
            <mat-icon svgIcon="check_circle_green" class="cleaning-result-icon"></mat-icon>
          </div>
          <h3 class="cleaning-state-title cleaning-state-title--success">Cleaning Complete</h3>
          
          <p class="cleaning-state-hint">
            <strong>Please Note:</strong> The cleaned data can be found alongside the original data in columns with a "_new" suffix.
          </p>
          <div class="cleaning-state-actions">
            <button *ngIf="cleaningResultData" mat-raised-button color="primary" class="cleaning-nav-btn" (click)="downloadCleaningResults()">
              <mat-icon>download</mat-icon>
              Download JSON Response
            </button>
            <button *ngIf="hasFileOutput" mat-raised-button color="primary" class="cleaning-nav-btn" (click)="onViewOutputs()">
              <mat-icon svgIcon="show-white"></mat-icon>
              View Outputs
            </button>
            <button mat-stroked-button class="cleaning-nav-btn" (click)="goToActivity()">
              <mat-icon svgIcon="docs"></mat-icon>
              Go to Activity
            </button>
          </div>

          <!-- Metrics Section - Collapsible -->
          <mat-expansion-panel class="cleaning-metrics-panel" *ngIf="auditEntries.length > 0" [expanded]="true">
            <mat-expansion-panel-header>
              <mat-panel-title class="metrics-title">
                <mat-icon class="metrics-icon">insights</mat-icon>
                <span>View Metrics - {{ goalMetrics.length }} goal{{ goalMetrics.length !== 1 ? 's' : '' }}, {{ getTotalTaskCount() }} task{{ getTotalTaskCount() !== 1 ? 's' : '' }}</span>
              </mat-panel-title>
            </mat-expansion-panel-header>
            
            <div class="metrics-content">
              <!-- Task Summary -->
              <div class="task-summary">
                <span class="task-count">{{ getFullRunMetrics().successCount }}/{{ auditEntries.length }} steps completed ({{ formatDuration(totalDuration) }})</span>
              </div>
              
              <!-- Timeline visualization -->
              <div class="timeline-container">
                <!-- Goal markers -->
                <div class="goal-markers">
                  <div *ngFor="let goalMetric of goalMetrics; let i = index" 
                       class="goal-marker"
                       [style.left.%]="getGoalBracePosition(goalMetric).left"
                       [style.z-index]="10 + i">
                    <div class="marker-line"></div>
                    <div class="marker-label">
                      <div class="goal-header">
                        <span class="goal-name">{{ goalMetric.goalName }}</span>
                        <span class="goal-duration">({{ formatDuration(goalMetric.totalDuration) }})</span>
                      </div>
                      <span class="goal-step-count">{{ goalMetric.steps.length }} step{{ goalMetric.steps.length !== 1 ? 's' : '' }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Timeline bar -->
                <div class="timeline-bar">
                  <div *ngFor="let block of timelineBlocks; let i = index"
                       class="timeline-block"
                       [class.selected]="selectedBlock === block.entry"
                       [class.dimmed]="selectedBlock && selectedBlock !== block.entry"
                       [style.left.%]="block.leftPercent"
                       [style.width.%]="block.widthPercent"
                       [matTooltip]="getBlockTooltip(block.entry)"
                       matTooltipClass="timeline-tooltip"
                       (click)="selectBlock(block.entry)">
                  </div>
                </div>
              </div>
              
              <!-- Block details bubble (always visible) -->
              <div class="block-details-bubble" *ngIf="selectedBlock">
                <div class="bubble-header">
                  <span class="bubble-title">{{ selectedBlock!.task_id }}</span>
                </div>
                <div class="bubble-content">
                  <div class="detail-row" *ngIf="selectedBlock!.inputs?.source">
                    <span class="detail-label">Source Column:</span>
                    <span class="detail-value">{{ selectedBlock!.inputs.source }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Duration:</span>
                    <span class="detail-value">{{ formatDuration(selectedBlock!.duration_ms) }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Rows Affected:</span>
                    <span class="detail-value">
                      {{ selectedBlock!.rows_affected !== null ? selectedBlock!.rows_affected : 0 }} 
                      / {{ selectedBlock!.input_rows }}
                      <span class="detail-percentage">({{ getRowsAffectedPercentage(selectedBlock!) }}%)</span>
                    </span>
                  </div>
                  <div class="detail-row" *ngIf="!selectedBlock!.status">
                    <span class="detail-label">Status:</span>
                    <span class="detail-value error-text">Failed</span>
                  </div>
                  <div class="detail-row" *ngIf="selectedBlock!.error_code">
                    <span class="detail-label">Error:</span>
                    <span class="detail-value error-text">{{ selectedBlock!.error_code }}</span>
                  </div>
                </div>
              </div>
            </div>
          </mat-expansion-panel>
        </ng-container>

        <!-- Error State -->
        <ng-container *ngIf="cleaningState === 'error'">
          <div class="cleaning-state-icon cleaning-state-icon--error">
            <mat-icon svgIcon="error" class="cleaning-result-icon"></mat-icon>
          </div>
          <h3 class="cleaning-state-title cleaning-state-title--error">Cleaning Failed</h3>
          <p class="cleaning-state-description">
            The workflow for <strong>{{ projectName }}</strong> encountered an error.
          </p>
          <p class="cleaning-state-error" *ngIf="error">{{ error }}</p>
          <div class="cleaning-state-actions">
            <button mat-raised-button color="primary" class="cleaning-nav-btn" (click)="onStartPipeline()">
              <mat-icon svgIcon="restart"></mat-icon>
              Try Again
            </button>
            <button mat-stroked-button class="cleaning-nav-btn" (click)="goToActivity()">
              <mat-icon svgIcon="docs"></mat-icon>
              Go to Activity
            </button>
          </div>
        </ng-container>

      </div>
    </ng-container>
  </div>
  </div>
</div>
